import _ from 'lodash';
import { useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LightThemeDialog } from 'common/components/LightThemeDialog';
import { PreferredThemeDialog } from 'common/components/PreferredThemeDialog';

import { USER_ROLES } from '@pwc/pwc-deals-web-common';
import { getUserRole } from 'common/redux/security/loggedInUser/loggedInUserSlice';

import sessionStorage from 'common/helpers/sessionStorage';
import { CUSTOM_COLOR_CONVERSION_KEY } from 'common/constants/General';
import {
  getProjectDefaultTheme,
  defaultColourConversion,
  getContentColourConversion,
  getShowColorConversionModal,
} from '../project/projectSlice';
import { getIsDarkModeSelector } from '../../common/redux/userSettings/userSettingsSlice';

const lightMode = '1';
const darkMode = '2';

type Props = {
  projectId: number | undefined;
  setIsPopupVisible: React.Dispatch<React.SetStateAction<boolean>>;
  isPopupVisible: boolean;
  switchUserTheme: (_isDarkMode: boolean) => Promise<void>;
  handleOnToggleClick: () => Promise<void>;
};

const DefaultTheme = ({
  projectId,
  setIsPopupVisible,
  isPopupVisible,
  switchUserTheme,
  handleOnToggleClick,
}: Props) => {
  const dispatch = useDispatch();

  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;

  const isUserThemeDarkMode = useSelector(getIsDarkModeSelector) as boolean;
  const colourConversion = useSelector(getContentColourConversion);
  const isCustomColourConversion =
    colourConversion.toLowerCase() !== defaultColourConversion.toLowerCase();

  /*
   * Get project default theme
   */
  const projectDefaultTheme = useSelector(getProjectDefaultTheme);
  const isProjectDefaultThemeLightMode = projectDefaultTheme === lightMode;
  const isProjectDefaultThemeDarkMode = projectDefaultTheme === darkMode;

  /*
   * Popup visibility
   */
  const [hasPopupBeenShown, setHasPopupBeenShown] = useState(false);
  const [isCustomColourPopUpVisible, setIsCustomColourPopUpVisible] =
    useState(false);
  const showColorConversionModal = useSelector(getShowColorConversionModal);

  useEffect(() => {
    sessionStorage.set(CUSTOM_COLOR_CONVERSION_KEY, defaultColourConversion);
  }, []);

  useEffect(() => {
    sessionStorage.set(CUSTOM_COLOR_CONVERSION_KEY, colourConversion);
  }, [isCustomColourConversion, colourConversion]);

  useEffect(() => {
    if (
      _.isBoolean(isUserThemeDarkMode) &&
      (isProjectDefaultThemeLightMode || isProjectDefaultThemeDarkMode)
    ) {
      if (
        !isPwcUser &&
        isProjectDefaultThemeDarkMode !== isUserThemeDarkMode &&
        !hasPopupBeenShown &&
        !isCustomColourConversion
      ) {
        setIsPopupVisible(true);
      }
      if (isProjectDefaultThemeDarkMode === isUserThemeDarkMode) {
        setHasPopupBeenShown(true);
      }
    }
  }, [
    dispatch,
    isPwcUser,
    projectId,
    isUserThemeDarkMode,
    hasPopupBeenShown,
    setHasPopupBeenShown,
    isProjectDefaultThemeLightMode,
    isProjectDefaultThemeDarkMode,
    setIsPopupVisible,
    isCustomColourConversion,
  ]);

  useEffect(() => {
    if (!projectId && hasPopupBeenShown) {
      setHasPopupBeenShown(false);
    }
  }, [projectId, setHasPopupBeenShown, hasPopupBeenShown]);

  useEffect(() => {
    if (!projectId && isPopupVisible) {
      setIsPopupVisible(false);
    }
  }, [projectId, isPopupVisible, setIsPopupVisible]);

  const handleOnDecline = useCallback(() => {
    if (_.isBoolean(isUserThemeDarkMode)) {
      setIsPopupVisible(false);
      setHasPopupBeenShown(true);
    }
  }, [setIsPopupVisible, isUserThemeDarkMode, setHasPopupBeenShown]);

  const handleOnAccept = useCallback(async () => {
    if (_.isBoolean(isUserThemeDarkMode)) {
      await switchUserTheme(!isUserThemeDarkMode);
      setIsPopupVisible(false);
      setHasPopupBeenShown(true);
    }
  }, [
    setIsPopupVisible,
    isUserThemeDarkMode,
    switchUserTheme,
    setHasPopupBeenShown,
  ]);

  const hidePopUp = () => {
    if (isPopupVisible) {
      setIsPopupVisible(false);
    }
  };

  const hideCustomColorModal = () => {
    setIsCustomColourPopUpVisible(false);
  };

  useEffect(() => {
    if (isCustomColourConversion && isUserThemeDarkMode) {
      hidePopUp();
      const updateUserTheme = async () => {
        await handleOnToggleClick();
      };
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      updateUserTheme();
      setIsCustomColourPopUpVisible(true);
    } else if (isCustomColourConversion && showColorConversionModal) {
      hidePopUp();
      setIsCustomColourPopUpVisible(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserThemeDarkMode, isCustomColourConversion, showColorConversionModal]);

  return (
    <>
      <PreferredThemeDialog
        open={isPopupVisible}
        onClose={() => {
          handleOnDecline();
        }}
        onConfirm={async () => {
          await handleOnAccept();
        }}
        data-testid="popover-default-theme"
      />

      <LightThemeDialog
        open={isCustomColourPopUpVisible}
        onClose={() => {
          hideCustomColorModal();
        }}
        onConfirm={() => {
          hideCustomColorModal();
        }}
      />
    </>
  );
};

export default DefaultTheme;
